import './child-bio-details.scss';

import { useRouter, useSearchParams } from 'next/navigation';
import { ElementType, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { HtmlBlock } from '@sola/ark-core/atoms/html/html.js';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { TabGroup } from '@sola/ark-core/molecules/tab-group/tab-group.js';
import { useCart } from '@sola/cart-sdk/store.js';
import { ErrorCart } from '@sola/schema/dotcom/cart/errors.js';
import {
  ChildAvailabilityItem,
  ChildDetails,
} from '@sola/schema/dotcom/children/child.js';
import { AvailabilityErrorTypeEnum } from '@sola/schema/dotcom/shared/am/errors.ts';
import { countryToIso } from '@sola/schema/dotcom/shared/geo.js';
import { trackError } from '@sola/tracking/components/datalayer.js';

import { key } from '../../organisms-cms/child-bio/utils/bioKeys';
import { getNote } from '../../organisms-cms/child-bio/utils/childNote';
import { getFamilyList } from '../../organisms-cms/child-bio/utils/familyList';
import { listFormat } from '../../organisms-cms/child-bio/utils/simpleList';
import { ChildBioDetailsOverview } from './child-bio-details-overview';

// TODO: Refactor to use for other calls to add items to the cart?
const BioErrors: React.FC<{ errors: ErrorCart[] }> = ({ errors }) => {
  const { t, i18n } = useTranslation(['common', 'bio']);
  const errorTypesDisplayed: string[] = [];
  const errorsToDisplay = errors.reduce((displayErrors: ErrorCart[], err) => {
    if (!errorTypesDisplayed.includes(err.type)) {
      errorTypesDisplayed.push(err.type);
      return [...displayErrors, err];
    }
    return displayErrors;
  }, []);
  return (
    <div className="flex flex-col gap-y-snug-xl mb-comfy-xl">
      {errorsToDisplay.map((error) => {
        trackError(JSON.stringify(error));
        // We may want to provide UI-specific properties in our error objects, but keeping things simple for now.
        const { type, severity } = error;
        const elKey = error.id;
        // TODO: Refactor cart errors out of checkout
        const hasMsg = i18n.exists(`checkout.errors.${type}.message`);
        const hasTitle = i18n.exists(`checkout.errors.${type}.title`);
        const message = hasMsg ? (
          <IntlBlock
            i18nKey={`checkout.errors.${type}.message`}
            values={error}
            tOptions={{
              interpolation: {
                escapeValue: false,
              },
            }}
            components={{
              // eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid
              anchor: <a />,
            }}
          />
        ) : (
          // TODO: Refactor cart errors out of checkout
          t(`checkout.errors.CART_GENERIC.message`)
        );
        const title = hasTitle ? (
          <IntlBlock
            i18nKey={`checkout.errors.${type}.title`}
            values={error}
            tOptions={{
              interpolation: {
                escapeValue: false,
              },
            }}
          />
        ) : undefined;

        return (
          (hasMsg || hasTitle) && (
            <Alert
              key={elKey}
              variant={severity ?? 'negative'}
              title={hasTitle && title}
              message={hasMsg && message}
              dismissible={false}
            />
          )
        );
      })}
    </div>
  );
};

export const ChildBioDetails = ({
  seoLevel = 1,
  details,
  availability,
}: {
  seoLevel?: number;
  details: ChildDetails;
  availability: ChildAvailabilityItem;
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bioErrors, setBioErrors] = useState<ErrorCart[]>();
  const { t } = useTranslation(['common', 'bio']);
  const router = useRouter();

  const searchParams = useSearchParams();
  const {
    actions: { addChild, goToCheckout },
  } = useCart('actions');

  const age = details.ageInYearsAndMonths.split(' Years')[0];

  const isToddler = age === '1' || age === '2';

  const countryIso = countryToIso(details.country, details.localId);
  const countryKey = String('common:geo.countries.')
    .concat(countryIso)
    .concat('.nameS');
  const translatedCountry = t(countryKey as unknown as TemplateStringsArray);
  const communityDescription = t('bio:community', { context: countryIso });
  const countryDescription = t('bio:description', { context: countryIso });
  const languageKey = key('bio:language', details.correspondenceLanguage);
  const translatedLanguage = t(languageKey as unknown as TemplateStringsArray);

  const note = getNote(details);

  const Comp = `h${seoLevel}` as ElementType;

  const handleSponsorshipSubmit = async () => {
    setIsSubmitting(true);
    setBioErrors(undefined);

    if (!details.globalId) {
      return;
    }
    // TODO: Add a catch for the event the global Id is not provided
    try {
      const errors = await addChild(
        details.globalId,
        // TODO: Would prefer these strings were in an enum
        searchParams.get('source'),
        searchParams.get('chid'),
      );

      if (errors && errors.length > 0) {
        // TODO: Maybe add a toast here
        // ...these types of errors do NOT persist (e.g. on the cart in Dynamo or in a persistent zustand store) in any way
        // ...for checkout to be aware of so they need to be handled here
        const hasAmHoldError = errors.some(
          (e) => e.type === AvailabilityErrorTypeEnum.ChildHold,
        );
        if (hasAmHoldError) {
          router.replace('/sponsor-a-child/not-found');
          return;
        }

        const errorsToDisplay = errors.filter((err) => !err.userShouldFix); // userShouldFix is a flag for checkout
        if (errorsToDisplay.length > 0) {
          setBioErrors(errorsToDisplay);
          return;
        }
      }

      // Don't set isSubmitting to false - keep the button disabled
      goToCheckout();
    } catch (error) {
      console.error('Error adding children to cart:', error);
      trackError(`Error adding children to cart: ${error}`);
    } finally {
      // Safari (and sometimes Chrome) doesn't always reset the button state if the user hits the back button from the Checkout page so we need to trigger
      // it here. The timeout of 1ms pushed the event to the end of the call stack, preventing a flicker on the button's state before navigation.
      setTimeout(() => {
        setIsSubmitting(false);
      }, 1);
    }
  };

  const overviewTab = {
    seoLevel: seoLevel + 1,
    title: t('bio:overview'),
    tabKey: 'overview',
    body: isToddler ? (
      <HtmlBlock
        htmlString={t('bio:babyParagraphs', {
          childName: details.name.preferred,
          koreanName: details.hangul.preferred || details.name.preferred,
        })}
      />
    ) : (
      <ChildBioDetailsOverview seoLevel={seoLevel} details={details} />
    ),
  };
  const communityTab = {
    seoLevel: seoLevel + 1,
    title: t('bio:community'),
    tabKey: 'community',
    body: <HtmlBlock htmlString={communityDescription as string} />,
  };
  const countryTab = {
    seoLevel: seoLevel + 1,
    title: t('bio:country'),
    tabKey: 'country',
    body: <HtmlBlock htmlString={countryDescription as string} />,
  };

  return (
    <div className="ChildBioDetails body-md xl:pr-comfy-xl grid gap-roomy-sm">
      <div className="ChildBioDetails__intro">
        <Comp className="ChildBioDetails__heading heading-potent heading-xl mb-comfy-sm">
          {t('bio:title', {
            childName: details.name.preferred,
            koreanName: details.hangul.preferred || details.name.preferred,
          })}
        </Comp>
        <div className="mb-comfy-sm">
          {t('bio:summary', {
            context: details.gender,
            childName: details.name.preferred,
            koreanName: details.hangul.preferred || details.name.preferred,
            age,
            country: translatedCountry,
            family: getFamilyList(details.householdList[0].householdMemberList),
            favoriteActivities:
              details.interests.thingsILike.length > 0
                ? t('bio:favoriteActivities', {
                    context: details.gender,
                    favorites: listFormat(details.interests.thingsILike),
                  })
                : '',
            projectActivities:
              details.interests.favoriteProjectActivity.length > 0
                ? t('bio:projectActivities', {
                    context: details.gender,
                    activities: listFormat(
                      details.interests.favoriteProjectActivity,
                    ),
                  })
                : '',
            language: translatedLanguage,
          })}
        </div>
        <div>
          <p>
            <span className="label-xl label-potent ChildBioDetails__label">
              {t('bio:age')}
            </span>
            {age}
          </p>
          <p>
            <span className="label-xl label-potent ChildBioDetails__label">
              {t('bio:birthday')}
            </span>
            {new Date(details.birthDate || '').toLocaleDateString(
              t('bio:localeFormat'),
              { year: 'numeric', month: 'long', day: 'numeric' },
            )}
            {details.isBirthDateEstimated && t('bio:estimatedBirthday')}
          </p>
          <p>
            <span className="label-xl label-potent ChildBioDetails__label">
              {t('bio:gender')}
            </span>
            {t('bio:genders', { context: details.gender.toString() })}
          </p>
          <p>
            <span className="label-xl label-potent ChildBioDetails__label">
              {t('bio:location')}
            </span>
            {t(countryKey as unknown as TemplateStringsArray)}
          </p>
          <p>
            <span className="label-xl label-potent ChildBioDetails__label">
              {t('bio:localId')}
            </span>
            {details.localId}
          </p>
          {note && (
            <p>
              <span className="label-xl label-potent ChildBioDetails__label">
                {t('bio:note')}
              </span>
              {note}
            </p>
          )}
        </div>
        <div className="mt-comfy-xl">
          {bioErrors && <BioErrors errors={bioErrors} />}
          <ButtonStyle variant="primary" isBusy={isSubmitting}>
            <button
              id="add-cdsp"
              className="w-full md:w-fit"
              type="button"
              onClick={handleSponsorshipSubmit}
            >
              {isSubmitting
                ? `${t('bio:processing')}...`
                : t('bio:sponsorButton', {
                    childName: details.name.preferred,
                    koreanName:
                      details.hangul.preferred || details.name.preferred,
                  })}
            </button>
          </ButtonStyle>
        </div>
      </div>
      <div className="ChildBioDetails__content">
        <TabGroup
          activeValue={overviewTab.tabKey}
          items={[overviewTab, communityTab, countryTab]}
        />
      </div>
    </div>
  );
};
